
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { CategoryGroup } from '@/hooks/useCategoryGroups';
import { useCommunity } from '@/contexts/CommunityContext';
import { 
  Home, 
  Briefcase, 
  Book, 
  ChevronDown, 
  ChevronUp,
  Heart,
  Users,
  GraduationCap,
  Car,
  Calendar,
  Camera,
  Dumbbell,
  Shield,
  MessageCircle,
  Building,
  UtensilsCrossed,
  ShoppingBag,
  Globe,
  Wrench,
  Gamepad2,
  UserCheck,
  Smartphone
} from 'lucide-react';
import { Button } from '@/components/ui/button';

interface CategoryGridProps {
  categoryGroups: CategoryGroup[];
  isLoading: boolean;
}

// Updated mapping to match exact database translation_key format with special characters
const getCategoryIcon = (translationKey: string) => {
  const exactIconMap: Record<string, React.ComponentType<any>> = {
    'category_group.adult_classes': Book,
    'category_group.children\'s_education': GraduationCap,
    'category_group.clothing_&_personal_care': Heart,
    'category_group.events_&_life_services': Calendar,
    'category_group.food_&_hospitality': UtensilsCrossed,
    'category_group.health_&_wellbeing': Heart,
    'category_group.home_&_property_services': Wrench,
    'category_group.language_&_communication': Globe,
    'category_group.legal_&_financial': Shield,
    'category_group.marketing_&_advertising': MessageCircle,
    'category_group.media_&_creative_services': Camera,
    'category_group.professional_&_business_services': Briefcase,
    'category_group.property_&_real_estate': Building,
    'category_group.religious_&_community_services': Users,
    'category_group.retail_&_handmade_goods': ShoppingBag,
    'category_group.security_services': UserCheck,
    'category_group.sports_&_fitness': Dumbbell,
    'category_group.tech_&_digital_services': Smartphone,
    'category_group.transport_&_travel': Car,
    'category_group.youth_activities': Gamepad2,
  };

  // Return exact match or default fallback
  return exactIconMap[translationKey] || Briefcase;
};

const CategoryGrid = ({ categoryGroups, isLoading }: CategoryGridProps) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { getParameter } = useCommunity();
  
  // Dynamic initial display count based on screen size
  const initialDisplayCount = isMobile ? 4 : 8;
  
  // Use displayCount instead of showAll boolean for progressive loading
  const [displayCount, setDisplayCount] = useState(initialDisplayCount);
  
  // Update displayCount when screen size changes
  useEffect(() => {
    setDisplayCount(initialDisplayCount);
  }, [initialDisplayCount]);
  
  const displayedCategories = categoryGroups.slice(0, displayCount);
  const hasMoreCategories = categoryGroups.length > displayCount;
  const isShowingAll = displayCount >= categoryGroups.length;

  const handleCategoryClick = (categoryGroupId: string) => {
    const selectedGroup = categoryGroups.find(group => group.id === categoryGroupId);
    if (selectedGroup && selectedGroup.categories && selectedGroup.categories.length > 0) {
      navigate(`/services?category=${selectedGroup.categories[0].id}`);
    } else {
      navigate('/services');
    }
  };

  const handleShowMore = () => {
    if (isMobile) {
      // Progressive loading on mobile: increment by 8
      setDisplayCount(prev => Math.min(prev + 8, categoryGroups.length));
    } else {
      // Desktop: show all remaining (current behavior)
      setDisplayCount(categoryGroups.length);
    }
  };

  const handleShowLess = () => {
    setDisplayCount(initialDisplayCount);
  };

  // Calculate remaining categories for button text
  const remainingCategories = categoryGroups.length - displayCount;
  const categoriesToShow = isMobile ? Math.min(8, remainingCategories) : remainingCategories;

  if (isLoading) {
    return (
      <div className="w-full">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {Array(initialDisplayCount).fill(0).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="min-h-24 md:min-h-28 bg-gray-200/50 rounded-xl border"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!categoryGroups || categoryGroups.length === 0) {
    return null;
  }

  return (
    <div className="w-full">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        <AnimatePresence>
          {displayedCategories.map((group, index) => {
            const IconComponent = getCategoryIcon(group.translation_key);
            
            return (
              <motion.button
                key={group.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.05, duration: 0.3 }}
                onClick={() => handleCategoryClick(group.id)}
                className="group flex flex-col items-center justify-center space-y-1 p-2 md:p-3 min-h-20 md:min-h-24 rounded-xl text-sm font-medium transition-all duration-300 border bg-white/40 text-gray-700 border-gray-200/50 hover:bg-white/80 hover:border-gray-300/70 hover:shadow-md hover:scale-[1.02]"
              >
                <IconComponent className="w-6 h-6 md:w-7 md:h-7 flex-shrink-0 group-hover:scale-110 transition-transform duration-200" />
                <span className="text-xs md:text-sm text-center leading-tight max-w-full break-words px-1">
                  {group.name}
                </span>
              </motion.button>
            );
          })}
        </AnimatePresence>
      </div>
      
      {(hasMoreCategories || (displayCount > initialDisplayCount && !isShowingAll)) && (
        <div className="flex justify-center">
          {hasMoreCategories ? (
            <Button
              variant="ghost"
              onClick={handleShowMore}
              className="text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100/50 transition-colors duration-200 flex items-center space-x-2 px-6 py-2 rounded-full"
            >
              <span>
                {isMobile 
                  ? `Show ${categoriesToShow} More Categories`
                  : `Show ${remainingCategories} More Categories`
                }
              </span>
              <ChevronDown className="w-4 h-4 transition-transform duration-200" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              onClick={handleShowLess}
              className="text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100/50 transition-colors duration-200 flex items-center space-x-2 px-6 py-2 rounded-full"
            >
              <span>Show Less</span>
              <ChevronUp className="w-4 h-4 transition-transform duration-200" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default CategoryGrid;
